import {
	Box,
	Grid,
	Dialog,
	Button,
	DialogContent,
	DialogTitle,
	useMediaQuery,
	useTheme,
	Divider,
	TextField,
	MenuItem,
	Hidden,
} from "@mui/material"
import { makeStyles } from "makeStyles"
import Icon from "@mdi/react"
import { mdiClose } from "@mdi/js"
import getLineItemTaxRate from "@secureo/common/utils/tax/getLineItemTaxRate"

import useInputValue from "hooks/useInputValue"
import useTranslation from "hooks/useTranslation"
import useTranslatedCountries from "hooks/useTranslatedCountries"

import AdditionalShippingInformation from "./AdditionalShippingInformation"
import CountryFlag from "components/i18n/CountryFlag"
import Price from "components/Price"

import { netToGross } from "@secureo/common/utils/tax/taxUtils"

import { shopCountryCodes } from "utils/i18n/supportedLocalesAndCurrencyCodes"
import {
	shippingZones,
	shippingZoneCostsByZoneNumber,
	// shippingCostMultiplierByCurrencyCode,
	flatFeesByCurrencyCode,
} from "@secureo/common/config/shippingZonesAndCosts"
import { getShippingCostsCents } from "@secureo/common/utils/shipping/getCartShippingCosts"

import { CountryCode } from "@secureo/common/typings/CountryCode"
import {
	DeliveryOptionsByDestinationCountryCode,
	DeliveryOption,
	ShippingOriginationCountryCodesByDestinationCountryCode,
} from "@secureo/common/typings/Product"
import { TFunction, Trans } from "next-i18next"
import { CurrencyCode } from "@secureo/common/typings/Forex"

const useStyles = makeStyles()((theme) => ({
	p: {
		color: "#536767",
	},
	price: {
		color: theme.palette.primary.main,
	},
}))

const i18nNameSpaces = ["countries", "product"]

const getHaulageDeliveryFallbackDeliveryOption = (
	destinationCountryCode: CountryCode,
	currencyCode: CurrencyCode,
	weightKg: number,
	t: TFunction,
) => {
	const name = t("product:haulageDelivery")
	const description = t("product:haulageDeliveryDescription")
	let netPriceEuroCents = 0

	const isShopCountry = shopCountryCodes.includes(destinationCountryCode)

	if (!isShopCountry) {
		// Calculate shippingCosts for depending on weight on destinationCountry
		const shippingZone = shippingZones[destinationCountryCode]
		const shippingZoneCosts = shippingZoneCostsByZoneNumber[shippingZone]
		netPriceEuroCents = getShippingCostsCents(weightKg, 1, shippingZoneCosts, currencyCode)
	}

	const deliveryOption = {
		name,
		description,
		netPriceEuroCents,
	}

	return deliveryOption
}

const getNonHaulageDeliveryFallbackDeliveryOption = (
	destinationCountryCode: CountryCode,
	currencyCode: CurrencyCode,
	t: TFunction,
) => {
	const name = t("product:packageDelivery")
	const description = t("product:packageDeliveryDescription")
	let netPriceEuroCents = 0
	let taxIsIncluded = false

	const isShopCountry = shopCountryCodes.includes(destinationCountryCode)

	if (!isShopCountry) {
		netPriceEuroCents = flatFeesByCurrencyCode[currencyCode].flatFeeForNonHaulageDeliveryCents
		taxIsIncluded = true // Flat fee is already in gross
	}

	const deliveryOption = {
		name,
		description,
		netPriceEuroCents,
		taxIsIncluded,
	}

	return deliveryOption
}

const getDeliveryOptions = (
	destinationCountryCode: CountryCode,
	currencyCode: CurrencyCode,
	isHaulageDelivery: boolean,
	deliveryOptions: DeliveryOptionsByDestinationCountryCode,
	weightKg: number,
	t: TFunction,
): (Partial<DeliveryOption> & { taxIsIncluded?: boolean })[] => {
	const deliveryOptionCountry = destinationCountryCode == "LI" ? "CH" : destinationCountryCode
	if (deliveryOptions?.[deliveryOptionCountry]) return deliveryOptions[deliveryOptionCountry]

	if (isHaulageDelivery)
		return [
			getHaulageDeliveryFallbackDeliveryOption(
				destinationCountryCode,
				currencyCode,
				weightKg,
				t,
			),
		]

	return [getNonHaulageDeliveryFallbackDeliveryOption(destinationCountryCode, currencyCode, t)]
}

const ShippingOption = ({
	name,
	description,
	isApplicableToFlatFeeForSmallAmountCents,
	grossPriceEuroCents,
	hideDivider,
	currencyCode,
}: ShippingOptionProps) => {
	const { classes } = useStyles()
	const { t } = useTranslation("product")

	const isFreeDeliveryOption = grossPriceEuroCents === 0

	return (
		<div>
			<strong>
				{name}{" "}
				<span className={classes.price}>
					{isFreeDeliveryOption ? (
						isApplicableToFlatFeeForSmallAmountCents ? (
							<Price
								priceInEuroCents={
									flatFeesByCurrencyCode[currencyCode].flatFeeForSmallAmountCents
								}
							/>
						) : (
							<span style={{ textTransform: "uppercase" }}>{t("free")}</span>
						)
					) : (
						<Price priceInEuroCents={grossPriceEuroCents} />
					)}
				</span>
			</strong>
			{description && <p className={classes.p}>{description}</p>}

			{!hideDivider && (
				<Box mb={2}>
					<Divider />
				</Box>
			)}
		</div>
	)
}

interface ShippingOptionProps {
	name: string
	description?: string
	isApplicableToFlatFeeForSmallAmountCents: boolean
	grossPriceEuroCents: number
	hideDivider: boolean
	currencyCode: CurrencyCode
}

const ShippingOptions = ({
	isOpen,
	toggleIsOpen,
	deliveryOptions,
	isHaulageDelivery,
	shippingOriginationCountryCodesByDestinationCountryCode,
	weightKg,
	grossProductPriceEuroCents,
	deliveryOptionsBySku,
	countryCode,
	currencyCode,
}: Props) => {
	const theme = useTheme()
	const fullScreen = useMediaQuery(theme.breakpoints.down("md"))

	const { t } = useTranslation(i18nNameSpaces)
	const selectedCountryCode = useInputValue(countryCode)
	const countries = useTranslatedCountries()

	const deliveryOptionsForDestinationCountryCode = getDeliveryOptions(
		selectedCountryCode.value as CountryCode,
		currencyCode,
		isHaulageDelivery,
		deliveryOptions,
		weightKg,
		t,
	)

	const shippingOriginationCountryCode =
		shippingOriginationCountryCodesByDestinationCountryCode?.[selectedCountryCode.value]

	const isShopCountry =
		shopCountryCodes.includes(selectedCountryCode.value as CountryCode) ||
		selectedCountryCode.value === "LI"
	const flatFeeForSmallAmountThresholdGrossCents =
		flatFeesByCurrencyCode[currencyCode].flatFeeForSmallAmountThresholdGrossCents
	const isApplicableToFlatFeeForSmallAmountCents =
		grossProductPriceEuroCents < flatFeeForSmallAmountThresholdGrossCents

	return (
		<Dialog
			fullScreen={fullScreen}
			fullWidth
			maxWidth={"md"}
			open={isOpen}
			onClose={toggleIsOpen}
			aria-labelledby="delivery-dialog"
		>
			<DialogTitle id="delivery-dialog-title">
				<div style={{ display: "flex", justifyContent: "space-between" }}>
					<div style={{ display: "flex", alignItems: "center" }}>
						<Box
							mr={1}
							style={{
								width: "25px",
								height: "100%",
							}}
						>
							<CountryFlag countryCode={selectedCountryCode.value as CountryCode} />
						</Box>
						<div style={{ marginTop: "-7px" }}>
							{t("product:shippingCostsOverview")}
						</div>
					</div>
					<Button onClick={toggleIsOpen}>
						<Icon path={mdiClose} size={1} color="black" />
					</Button>
				</div>
				<Box mt={1}>
					<Divider />
				</Box>
			</DialogTitle>
			<DialogContent>
				<Grid container spacing={3}>
					<Grid item xs={12} md={4}>
						<div>
							<strong>{t("product:calculateDeliveryFor")}:</strong>
						</div>
						<TextField
							id="outlined-select-currency"
							select
							label={t("countries:country")}
							margin="normal"
							variant="outlined"
							{...selectedCountryCode}
							fullWidth
							inputProps={{
								name: "country",
								id: "country",
							}}
						>
							{countries.map(({ countryCode, name }) => (
								<MenuItem key={countryCode} value={countryCode}>
									{name}
								</MenuItem>
							))}
						</TextField>
						<Hidden mdDown>
							<AdditionalShippingInformation />
						</Hidden>
					</Grid>
					<Grid item xs={12} md={8}>
						{deliveryOptionsForDestinationCountryCode.map((deliveryOption, i) => {
							const { sku, netPriceEuroCents, taxIsIncluded = false } = deliveryOption

							const name =
								deliveryOptionsBySku[sku]?.name ?? deliveryOption?.name ?? ""
							const description =
								deliveryOptionsBySku[sku]?.description ??
								deliveryOption?.description ??
								""

							const { taxRate } = getLineItemTaxRate(
								shippingOriginationCountryCode,
								selectedCountryCode.value as CountryCode,
							)
							const grossPriceEuroCents = taxIsIncluded
								? netPriceEuroCents
								: Math.round(netToGross(netPriceEuroCents, taxRate))

							const isLastElement =
								i === deliveryOptionsForDestinationCountryCode.length - 1

							return (
								<ShippingOption
									key={sku || name}
									name={name || sku}
									description={description}
									grossPriceEuroCents={grossPriceEuroCents}
									isApplicableToFlatFeeForSmallAmountCents={
										isApplicableToFlatFeeForSmallAmountCents
									}
									hideDivider={isLastElement}
									currencyCode={currencyCode}
								/>
							)
						})}
					</Grid>
					<Hidden mdUp>
						<AdditionalShippingInformation />
					</Hidden>
					<Grid item xs={12}>
						<Divider />
						{isHaulageDelivery && isShopCountry && (
							<p>{t("product:deliveryOptionsCanBeAddedInCheckout")}</p>
						)}
						{!isShopCountry && <p>{t("product:deliveryOptionsCouldBeHigher")}</p>}
						{isShopCountry && isApplicableToFlatFeeForSmallAmountCents && (
							<p>
								<Trans
									t={t}
									i18nKey="product:flatFeeNotice"
									components={{
										FlatFee: (
											<Price
												priceInEuroCents={
													flatFeesByCurrencyCode[currencyCode]
														.flatFeeForSmallAmountCents
												}
											/>
										),
										Threshold: (
											<Price
												priceInEuroCents={
													flatFeeForSmallAmountThresholdGrossCents
												}
											/>
										),
									}}
								/>
							</p>
						)}
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	)
}

interface Props {
	isOpen: boolean
	toggleIsOpen: () => void
	isHaulageDelivery: boolean
	deliveryOptions: DeliveryOptionsByDestinationCountryCode
	shippingOriginationCountryCodesByDestinationCountryCode: ShippingOriginationCountryCodesByDestinationCountryCode
	grossProductPriceEuroCents: number
	weightKg: number
	deliveryOptionsBySku: {
		[sku: string]: DeliveryOption
	}
	countryCode: CountryCode
	currencyCode: CurrencyCode
}

export default ShippingOptions
